import React, { useEffect, useState, useCallback } from 'react';
import { Button, Col, Container, Form, Row, FloatingLabel, OverlayTrigger, Tooltip } from 'react-bootstrap';
import styles from '../NewOrganization.module.css';
import managementStyles from '../../ManagementPages.module.css';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { resetNewOrganization, setFormValue } from '../NewOrganizationSlicer';
import useAxios from '../../../services/AxiosConfig';
import FileUploadOrganization from './FileUploadOrganization';
import { showAlert } from '../../../components/PerinHealthAlertSlice';
import { refreshUserGroupsAndHierarchy } from '../../../utils/sidebarUtils';
import { closeModal, openModal } from '../../../components/PerinHealthModelSlice';
import { resetDirty, setDirty } from '../../../slicers/DirtyCheckSlice';
import PerinHealthModal from '../../../components/PerinHealthModel';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const AddNewOrganization = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const axiosConfig = useSelector((state) => state.axiosConfig);
    const httpService = useAxios(axiosConfig.envURL);
    const { isAdmin, id } = useSelector((state) => state.userInfo);
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);

    const newOrganization = useSelector((state) => state.newOrganization);
    const perinHealthGrid = useSelector((state) => state.perinHealthGrid);
    const selectedItem = perinHealthGrid.selectedItem;
    const isUpdateMode = newOrganization.isUpdateMode;
    const isDirty = useSelector((state) => state.dirty.isDirty);

    useEffect(() => {
    }, [selectedItem, dispatch]);

    const countryCodes = [
        { value: '+1', label: '+1 (US)' },
        { value: '+44', label: '+44 (UK)' },
    ];

    const handleControlChange = (name, value) => {
        dispatch(setDirty());
        dispatch(setFormValue({ name, value }));
    };


    const onBackHandler = () => {
        if (isDirty) {
            dispatch(
                openModal({
                    activeModalId: "AddNewOrganizationMain",
                    header: 'Confirmation',
                    message: "You have unsaved changes. Are you sure you want to leave?",
                    buttons: [
                        { label: 'No', variant: 'secondary', onClickKey: 'NO' },
                        { label: 'Yes', variant: 'primary', onClickKey: 'YES' },
                    ],
                })
            );
        }else {
            navigate('/new-organization');
        }
    };

    const handleDialogButtonClick = (onClickKey) => {
        if (onClickKey === 'YES') {
            navigate('/new-organization');
            dispatch(resetDirty());
        }
        dispatch(closeModal());
    };

    const OnAddNewOrganization = () => {
        const formData = formatDataForSaving();
    
        // Create a new FormData object
        const formDataWithFile = new FormData();
    
        // Append JSON data
        formDataWithFile.append('data', JSON.stringify(formData));
    
        // Append the file
        if (selectedFile) {
            formDataWithFile.append('file', selectedFile);
        }
    
        const endpoint = isUpdateMode ? `organization/${formData.id}` : 'organization';
        httpService({
            method: isUpdateMode ? 'put' : 'post',
            url: endpoint,
            data: formDataWithFile,
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        })
        .then((response) => {
            console.log(`Organization ${isUpdateMode ? 'updated' : 'added'} successfully:`, response.data);
            dispatch(resetNewOrganization());
            refreshUserGroupsAndHierarchy(httpService, id, isAdmin, dispatch);
            navigate('/new-organization');
            dispatch(
                showAlert({
                    header: "Success",
                    message: `Organization ${isUpdateMode ? 'updated' : 'added'} successfully:`,
                    type: 'success'
                })
            );
        })
        .catch((error) => {
            console.error(`Error ${isUpdateMode ? 'updating' : 'adding'} Organization:`, error);
            dispatch(
                showAlert({
                    header: "Error",
                    message: `Error ${isUpdateMode ? 'updating' : 'adding'} Organization`,
                    type: 'danger'
                })
            );
        });
    };

    const formatDataForSaving = () => {
        return removeUndefined(newOrganization);
    };

    const removeUndefined = (obj) => {
        const excludedAttributes = ['isUpdateMode', 'logoId', 'selectAll', 'teamName', 'subGroupName', 'selectedRows', 'reloadGrid', 'organizationStatus', 'logoURL', 'logoId', 'groupName'];
        if (!isUpdateMode) {
            excludedAttributes.push('id');
        }

        const cleanedData = Object.fromEntries(
            Object.entries(obj).map(([key, value]) => {
                if (!excludedAttributes.includes(key) && value !== undefined) {
                    return [key, value];
                }
                return null;
            }).filter(entry => entry !== null)
        );

        return cleanedData;
    };

    const handleLogoDrop = (acceptedFiles) => {
        const logoFile = acceptedFiles[0];
        handleControlChange('logoId', "i123");

        if (logoFile) {
            setSelectedFile(logoFile);
            const objectURL = URL.createObjectURL(logoFile);
            handleControlChange('logoURL', objectURL);
        }
    };


    const PasswordTooltip = (
        <Tooltip id="tooltip-password-policy">
            Determines how often users are required to reset their password for security purposes.
        </Tooltip>
    );

    const InactivityTooltip = (
        <Tooltip id="tooltip-inactivity">
            Sets the amount of time a user can remain inactive before being automatically signed out.
        </Tooltip>
    );

    const ArchiveTooltip = (
        <Tooltip id="tooltip-inactivity">
           Defines how long archived data will remain accessible before being permanently removed.
        </Tooltip>
    );

    return (
        <div className={managementStyles.MainCard}>
            <h3 className={managementStyles.ContentTitle}>{isUpdateMode ? 'Update ' : 'Add '} Organization</h3>
            <Form className={managementStyles.HideScrollContainer}>
                <Form.Group className="mb-3">
                    <FloatingLabel 
                        controlId="floatingInput"
                        label="Organization Name*"
                        className="mb-3"
                    >
                    <Form.Control
                        type="text"
                        placeholder="name"
                        style={{width:"300px"}}
                        value={newOrganization.name}
                        className={`${managementStyles.BorderedComponent}`}
                        onChange={(e) => handleControlChange('name', e.target.value)}
                    />
                    </FloatingLabel>
                </Form.Group>

                <Form.Group className="mb-3">
                    <FloatingLabel 
                        controlId="floatingInput"
                        label="Email*"
                        className="mb-3"
                    >
                    <Form.Control
                        type="text"
                        placeholder="email"
                        style={{width:"300px"}}
                        value={newOrganization.email}
                        className={`${managementStyles.BorderedComponent}`}
                        onChange={(e) => handleControlChange('email', e.target.value)}
                    />
                    </FloatingLabel>
                </Form.Group>

                <Form.Group style={{width:"300px"}} className="mb-3">
                    <Row>
                        <Col xs={4}>
                            <FloatingLabel controlId="floatingSelect" label="Country Code">
                            <Form.Select
                                style={{width:"100px"}}
                                name="code"
                                value={countryCodes.find(
                                    (option) => option.value === newOrganization.countryCode
                                )}
                                onChange={(selectedOption) =>
                                    handleControlChange('countryCode', selectedOption.value)
                                }
                                className={`${managementStyles.BorderedComponent}`}
                            >
                                <option value="">Select</option>
                                {countryCodes.map((countryCode) =>
                                    <option key={countryCode.value} value={countryCode.value}>
                                        {countryCode.label}
                                    </option>
                                )}
                            </Form.Select>
                            </FloatingLabel>
                        </Col>
                        <Col xs={8}>
                            <FloatingLabel controlId="floatingSelect" label="Phone Number*">
                            <Form.Control
                                style={{width:"100%"}}
                                type="phone"
                                placeholder={''}
                                value={newOrganization.phone}
                                className={`${managementStyles.BorderedComponent}`}
                                onChange={(e) =>
                                    handleControlChange('phone', e.target.value)
                                }
                            />
                            </FloatingLabel>
                        </Col>
                    </Row>
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label className="headline-6">Organization Label</Form.Label>
                    <FloatingLabel controlId="floatingSelect" label="Group Name*">
                    <Form.Control
                        placeholder="Group Name"
                        style={{width:"300px"}}
                        value={newOrganization.groupName}
                        className={`${managementStyles.BorderedComponent}`}
                        onChange={(e) => {
                            handleControlChange('groupName', e.target.value);
                        }}
                    />
                    </FloatingLabel>
                </Form.Group>

                <Form.Group className="mb-3">
                    <FloatingLabel controlId="floatingSelect" label="Subgroup Name*">
                    <Form.Control
                        placeholder="Subgroup Name"
                        type="text"
                        style={{width:"300px"}}
                        value={newOrganization.subGroupName}
                        className={`${managementStyles.BorderedComponent}`}
                        onChange={(e) => handleControlChange('subGroupName', e.target.value)}
                    />
                    </FloatingLabel>
                </Form.Group>

                <Form.Group className="mb-3">
                    <FloatingLabel controlId="floatingSelect" label="Team Name*">
                    <Form.Control
                        placeholder="Team Name"
                        type="text"
                        style={{width:"300px"}}
                        value={newOrganization.teamName}
                        className={`${managementStyles.BorderedComponent}`}
                        onChange={(e) => handleControlChange('teamName', e.target.value)}
                    />
                    </FloatingLabel>
                </Form.Group>
                <Form.Group className="mb-3" style={{width:"300px"}}>
                    <Form.Label className="headline-6">Upload Logo</Form.Label>
                    <FileUploadOrganization
                        fileType='image'
                        onDrop={handleLogoDrop}
                        imageURL={newOrganization.logoURL} 
                        acceptedTypes={"PNG,JPG | 8MB Max"}
                    />
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label className="headline-6" as="legend" column sm={6}>
                        Organization Status
                    </Form.Label>
                        <Form.Group>
                            <Form.Check
                                className={styles.FormCheck}
                                type="switch"
                                label="Active"
                                checked={newOrganization.organizationStatus}
                                onChange={(e) => handleControlChange('organizationStatus', e.target.checked)}
                            />
                        </Form.Group>
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label className="headline-6" as="legend" column sm={6}>
                        Password Policy
                        <OverlayTrigger placement="right" overlay={PasswordTooltip}>
                            <FontAwesomeIcon icon="fa-solid fa-circle-info" style={{ color: '#686869', paddingLeft:"10px" }} />
                        </OverlayTrigger>
                    </Form.Label>
                    <Row className="align-items-center">
                        <Col xs="auto">
                            <FloatingLabel controlId="floatingSelect" label="Password Expiry Frequency*">
                                <Form.Select
                                    style={{width:"200px"}}
                                    value={newOrganization.passwordResetDays}
                                    onChange={(e) => handleControlChange('passwordResetDays', parseInt(e.target.value))}
                                    className={`${managementStyles.BorderedComponent}`}
                                >
                                    <option value={30}>30 days</option>
                                    <option value={60}>60 days</option>
                                    <option value={90}>90 days</option>
                                    <option value={180}>6 months</option>
                                    <option value={365}>1 year</option>
                                    <option value={730}>2 years</option>
                                    <option value={365000}>Never expire</option>
                                </Form.Select>
                            </FloatingLabel>
                        </Col>
                    </Row>
                </Form.Group>
                <Form.Group className="mb-3">
                    <Form.Label className="headline-6" as="legend" column sm={6}>
                        Inactivity
                        <OverlayTrigger placement="right" overlay={InactivityTooltip}>
                            <FontAwesomeIcon icon="fa-solid fa-circle-info" style={{ color: '#686869', paddingLeft:"10px" }} />
                        </OverlayTrigger>
                    </Form.Label>
                    <Row className="align-items-center">
                        <Col xs="auto">
                            <FloatingLabel controlId="floatingSelect" label="Inactivity Timeout*">
                                <Form.Select
                                    style={{width:"200px"}}
                                    value={newOrganization.inactivityTimeout}
                                    onChange={(e) => handleControlChange('inactivityTimeout', parseInt(e.target.value))}
                                    className={`${managementStyles.BorderedComponent}`}
                                >
                                    <option value={5}>5 minutes</option>
                                    <option value={10}>10 minutes</option>
                                    <option value={15}>15 minutes</option>
                                    <option value={20}>20 minutes</option>
                                    <option value={25}>25 minutes</option>
                                    <option value={30}>30 minutes</option>
                                </Form.Select>
                            </FloatingLabel>
                        </Col>
                    </Row>
                </Form.Group>
                <Form.Group className="mb-3">
                    <Form.Label className="headline-6" as="legend" column sm={6}>
                        Archive Settings
                        <OverlayTrigger placement="right" overlay={ArchiveTooltip}>
                            <FontAwesomeIcon icon="fa-solid fa-circle-info" style={{ color: '#686869', paddingLeft:"10px" }} />
                        </OverlayTrigger>
                    </Form.Label>
                    <Row className="align-items-center">
                        <Col xs="auto">
                            <Form.Check
                                className={styles.FormCheck}
                                type="switch"
                                label="Archive Enabled"
                                checked={newOrganization.archiveEnabled}
                                onChange={(e) => handleControlChange('archiveEnabled', e.target.checked)}
                            />
                        </Col>
                        {newOrganization.archiveEnabled && (
                            <Col xs="auto">
                                <FloatingLabel controlId="floatingSelect" label="Archive Inactive Time*">
                                    <Form.Select
                                        style={{width:"200px"}}
                                        value={newOrganization.archiveInactiveDays}
                                        onChange={(e) => handleControlChange('archiveInactiveDays', parseInt(e.target.value))}
                                        className={`${managementStyles.BorderedComponent}`}
                                    >
                                        <option value={365}>1 year</option>
                                        <option value={730}>2 years</option>
                                        <option value={1095}>3 years</option>
                                        <option value={1460}>4 years</option>
                                        <option value={1825}>5 years</option>
                                    </Form.Select>
                                </FloatingLabel>
                            </Col>
                        )}
                    </Row>
                </Form.Group>
            </Form>
            <div className={managementStyles.ButtonsFooterContainer}>
                <Row>
                    <Col>
                        <Button variant="primary" className={managementStyles.CreateButton} onClick={OnAddNewOrganization}>
                            {isUpdateMode ? 'Update ' : 'Add '}
                        </Button>
                    </Col>
                    <Col>
                        <Button variant="secondary" className={managementStyles.BackButton} onClick={onBackHandler}>
                            Cancel
                        </Button>
                    </Col>
                </Row>
            </div>
            <PerinHealthModal
                modalId={"AddNewOrganizationMain"}
                handleButtonClick={handleDialogButtonClick}
            />
        </div>
    );
};

export default AddNewOrganization;
